import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/services/api';
import LoadingSpinner from '@/shared/ui/feedback/LoadingSpinner';
import ErrorMessage from '@/shared/ui/feedback/ErrorMessage';
import PageLayout from '@/shared/ui/layout/PageLayout';
import TitleWithSelector from '@/shared/ui/layout/TitleWithSelector';
import './styles.css';

interface DashboardTestDTO {
  _id: string;
  name: string;
  priority: number;
}

interface PerformanceDetail {
  score: number;
  rank: number;
}

interface ModelPerformanceDTO {
  modelName: string;
  evaluationId: string;
  evaluationDate: string;
  categories: {
    [category: string]: PerformanceDetail;
  };
  color?: string;
}

interface DashboardResultsDTO {
  testName: string;
  allCategories: string[];
  performanceData: ModelPerformanceDTO[];
}

const Dashboard: React.FC = () => {
  const [tests, setTests] = useState<DashboardTestDTO[]>([]);
  const [selectedTest, setSelectedTest] = useState<string>('');
  const [results, setResults] = useState<DashboardResultsDTO | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('Overall');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTests = async () => {
    try {
      setLoading(true);
      const testsData = await api.dashboard.getDashboardTests();
      setTests(testsData);
      if (testsData.length > 0 && !selectedTest) {
        setSelectedTest(testsData[0]._id);
      } else {
        setLoading(false);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard tests:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch tests');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTests();
  }, []);

  useEffect(() => {
    const fetchResults = async (testId: string) => {
      try {
        setLoading(true);
        const resultsData: DashboardResultsDTO = await api.dashboard.getDashboardResults(testId);

        setResults(resultsData);
        setError(null);
      } catch (err) {
        console.error(`Error fetching dashboard results for test ${testId}:`, err);
        setError(err instanceof Error ? err.message : 'Failed to fetch results');
        setResults(null);
      } finally {
        setLoading(false);
      }
    };

    if (selectedTest) {
        setSelectedCategory('Overall');
        fetchResults(selectedTest);
    }
  }, [selectedTest]);
  
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
  };

  const modelPerformances = results?.performanceData
    .map(p => ({
      modelName: p.modelName,
      evaluationId: p.evaluationId,
      evaluationDate: new Date(p.evaluationDate),
      score: p.categories[selectedCategory]?.score ?? 0,
      rank: p.categories[selectedCategory]?.rank ?? 0,
      color: p.color,
    }))
    .sort((a, b) => a.rank - b.rank)
    ?? [];

  const fetchInitialData = () => {
    setSelectedTest('');
    fetchTests();
  };

  if (loading && !results) return <LoadingSpinner message="Loading dashboard..." />;
  if (error) return <ErrorMessage message={error} onRetry={fetchInitialData} />;

  if (tests.length === 0 && !loading) {
    return (
      <PageLayout
        title="Leaderboard"
        actions={<Link to="/tests/new" className="button primary">Create a Test</Link>}
      >
        <div className="no-data">
          <p>No tests available yet.</p>
          <p>Create a test and complete some evaluations to see performance metrics.</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={
        <TitleWithSelector
          title="Leaderboard"
          options={tests.map(t => ({ value: t._id, label: t.name }))}
          selectedValue={selectedTest}
          onChange={setSelectedTest}
        />
      }
      headerContent={results && (
        <>
          <div className="category-tabs">
            {results.allCategories.map(category => (
              <button
                key={category}
                className={`category-tab ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => handleCategoryChange(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </>
      )}
    >
      {!results || modelPerformances.length === 0 ? (
        <div className="no-data">
          <p>No evaluations available for test: {results?.testName}</p>
          <p>Complete an evaluation for this test to see performance metrics.</p>
          <Link to={`/tests/${selectedTest}/evaluate`} className="button primary">
            Evaluate This Test
          </Link>
        </div>
      ) : (
        <div className="performance-chart">
          <h3>
            {selectedCategory === 'Overall' ? 'Overall Performance' : `Performance in ${selectedCategory}`}
          </h3>
            <div className="bar-chart">
               <div className="bars-container">
                <div className="percentage-grid">
                  <div className="grid-line" style={{ left: '0%' }}></div>
                  <div className="grid-line" style={{ left: '20%' }}></div>
                  <div className="grid-line" style={{ left: '40%' }}></div>
                  <div className="grid-line" style={{ left: '60%' }}></div>
                  <div className="grid-line" style={{ left: '80%' }}></div>
                  <div className="grid-line" style={{ left: '100%' }}></div>
                </div>
                <div className="percentage-labels">
                  <div className="percent-label" style={{ left: '0%' }}>0%</div>
                  <div className="percent-label" style={{ left: '20%' }}>20%</div>
                  <div className="percent-label" style={{ left: '40%' }}>40%</div>
                  <div className="percent-label" style={{ left: '60%' }}>60%</div>
                  <div className="percent-label" style={{ left: '80%' }}>80%</div>
                  <div className="percent-label" style={{ left: '100%' }}>100%</div>
                </div>
                {modelPerformances.map(performance => (
                  <div key={performance.modelName} className="chart-bar-container">
                    <div className="model-name">{performance.modelName}</div>
                    <div className="bar-wrapper">
                      <div
                        className="bar"
                        style={{
                          width: `${performance.score}%`,
                          backgroundColor: performance.color || '#cccccc',
                        }}
                      >
                        <span className="bar-label">{performance.score.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="performance-table-container">
              <table className="performance-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Model</th>
                    <th>Score</th>
                    <th>Evaluation Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {modelPerformances.map(performance => (
                    <tr key={performance.modelName}>
                      <td>{performance.rank}</td>
                      <td>
                        <div className="model-name-with-color">
                          <span
                            className="color-indicator"
                            style={{ backgroundColor: performance.color || '#cccccc' }}
                          ></span>
                          {performance.modelName}
                        </div>
                      </td>
                      <td className="score-cell">{performance.score.toFixed(1)}%</td>
                      <td>{performance.evaluationDate.toLocaleDateString()}</td>
                      <td>
                        <Link
                          to={`/evaluations/${performance.evaluationId}`}
                          className="button small"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
        </div>
      )}
    </PageLayout>
  );
};

export default Dashboard; 